import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Cpu, Search, Fingerprint, Lock, ShieldAlert, Zap, ArrowRight, Layers } from 'lucide-react';

const ComplianceEngine: React.FC = () => {
    const navigate = useNavigate();

    const handleRunAI = () => {
        navigate('/eligibility?service=immigration');
    };

    return (
        <div className="bg-slate-50 min-h-screen pt-32 pb-20 font-sans">
            {/* Header */}
            <div className="max-w-7xl mx-auto px-6 mb-20 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-[#011E26] text-xs font-bold uppercase tracking-widest mb-8 shadow-sm border border-slate-200">
                    <Cpu size={14} className="text-[#009B8F]" /> Automated Review System
                </div>
                <h1 className="text-5xl md:text-7xl font-black text-[#011E26] mb-6 tracking-tight">
                    Automated <span className="text-[#009B8F]">Compliance</span> <br /> Engine
                </h1>
                <p className="text-xl text-slate-500 max-w-2xl mx-auto font-light leading-relaxed mb-10">
                    Analyze your personal and financial data against publicly available visa rules within seconds.
                </p>
                <button
                    onClick={handleRunAI}
                    className="group bg-[#009B8F] text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-[#011E26] transition-all flex items-center gap-3 mx-auto shadow-xl"
                >
                    Run AI Check
                    <ArrowRight className="group-hover:translate-x-2 transition-transform" />
                </button>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20 items-stretch">
                    <div className="bg-white rounded-[3rem] p-12 shadow-xl border border-slate-100 flex flex-col justify-center">
                        <h2 className="text-3xl font-bold text-[#011E26] mb-8">Structured, Automated Review.</h2>
                        <div className="space-y-6 text-slate-600 text-lg">
                            <p>
                                As you enter your details, the engine analyses your information using pattern‐based logic
                                and rule‐matching techniques.
                            </p>
                            <p>
                                It checks whether your data aligns with essential criteria commonly associated with
                                successful applications, such as financial maintenance and academic consistency.
                            </p>
                        </div>
                    </div>
                    <div className="relative">
                        <img
                            src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop"
                            alt="Digital Security"
                            className="rounded-[3rem] shadow-2xl h-full object-cover"
                        />
                        <div className="absolute inset-x-12 bottom-12 p-8 bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 text-white">
                            <ShieldAlert className="mb-4" />
                            <p className="text-sm italic">"Identifies general risk indicators early so you can address them before submission."</p>
                        </div>
                    </div>
                </div>

                {/* Feature Grid */}
                <div className="mb-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[
                            {
                                icon: <Layers className="w-8 h-8 text-[#009B8F]" />,
                                title: "Intelligent Data Mapping",
                                desc: "Your inputs are mapped against key eligibility criteria to help you understand your profile alignment."
                            },
                            {
                                icon: <Zap className="w-8 h-8 text-[#009B8F]" />,
                                title: "Instant Discrepancy Flagging",
                                desc: "If the system detects missing info or inconsistencies, it highlights them immediately."
                            },
                            {
                                icon: <Lock className="w-8 h-8 text-[#009B8F]" />,
                                title: "Zero-Document Processing",
                                desc: "Privacy-first approach. The engine works entirely on data points, no file uploads needed."
                            },
                            {
                                icon: <Search className="w-8 h-8 text-[#009B8F]" />,
                                title: "AI-Run Rule Check",
                                desc: "An automated technical comparison between your inputs and publicly available visa rules."
                            }
                        ].map((card, i) => (
                            <div key={i} className="bg-white p-10 rounded-3xl shadow-sm border border-slate-200 flex gap-8 group hover:shadow-lg transition-all">
                                <div className="p-4 bg-slate-50 rounded-2xl group-hover:bg-[#009B8F]/10 transition-colors h-fit">
                                    {card.icon}
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-[#011E26] mb-4">{card.title}</h3>
                                    <p className="text-slate-500 leading-relaxed">{card.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Disclaimer Footer */}
                <div className="bg-[#011E26] rounded-[3rem] p-12 md:p-16 text-white text-center shadow-2xl">
                    <Fingerprint className="w-12 h-12 text-[#009B8F] mx-auto mb-8 opacity-50" />
                    <h3 className="text-3xl font-bold mb-6 italic font-serif tracking-wide">Disclaimer</h3>
                    <p className="text-slate-400 text-lg max-w-3xl mx-auto mb-10 leading-relaxed">
                        This feature performs an automated comparison against publicly available rules and
                        does not provide legal advice. For personalised guidance, users may choose to consult
                        a regulated immigration professional.
                    </p>
                    <button
                        onClick={handleRunAI}
                        className="bg-white text-[#011E26] px-10 py-4 rounded-xl font-bold hover:bg-[#009B8F] hover:text-white transition-all shadow-lg"
                    >
                        Initiate Automated Review
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComplianceEngine;
